import { Node } from "@xyflow/react";
import { createEdge, Edge } from "./Edge";
import { NodeType } from "./NodeType";
import { EdgeType } from "./EdgeType";

/**
 * Build adjacency list from edges for efficient graph traversal
 */
function buildAdjacencyList(edges: Map<string, Edge>): Map<string, string[]> {
  const adjacencyList = new Map<string, string[]>();

  for (const edge of edges.values()) {
    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, []);
    }
    adjacencyList.get(edge.source)!.push(edge.target);
  }

  return adjacencyList;
}

/**
 * Add edge to adjacency list
 */
function addEdgeToAdjacencyList(
  adjacencyList: Map<string, string[]>,
  sourceId: string,
  targetId: string,
): void {
  if (!adjacencyList.has(sourceId)) {
    adjacencyList.set(sourceId, []);
  }
  adjacencyList.get(sourceId)!.push(targetId);
}

/**
 * Remove edge from adjacency list
 */
function removeEdgeFromAdjacencyList(
  adjacencyList: Map<string, string[]>,
  sourceId: string,
  targetId: string,
): void {
  const neighbors = adjacencyList.get(sourceId);
  if (neighbors) {
    const index = neighbors.indexOf(targetId);
    if (index > -1) {
      neighbors.splice(index, 1);
    }
    if (neighbors.length === 0) {
      adjacencyList.delete(sourceId);
    }
  }
}

/**
 * Optimized cycle detection using DFS with adjacency list
 */
function wouldCreateCycle(
  adjacencyList: Map<string, string[]>,
  sourceId: string,
  targetId: string,
): boolean {
  if (sourceId === targetId) {
    return true;
  }

  const visited = new Set<string>();
  const stack = [targetId];

  while (stack.length > 0) {
    const current = stack.pop()!;

    if (current === sourceId) {
      return true;
    }
    if (visited.has(current)) {
      continue;
    }

    visited.add(current);

    // Get neighbors from adjacency list (O(1) lookup)
    const neighbors = adjacencyList.get(current) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        stack.push(neighbor);
      }
    }
  }

  return false;
}

/**
 * Remap edges that connect nodes between different hierarchy levels.
 *
 * This function handles collapsed groups by remapping edges from group children
 * to their parent group nodes, while ensuring no cycles are created in the process.
 */
export function remapCollapsedGroupEdges(nodes: Map<string, Node>, edges: Map<string, Edge>): void {
  // Build adjacency list once for efficient cycle detection
  const adjacencyList = buildAdjacencyList(edges);
  const collapsed = new Set(
    Array.from(nodes.values())
      .filter((node) => node.type === NodeType.Group && !node.expandParent)
      .map((node) => node.id),
  );

  if (collapsed.size === 0) {
    return;
  }

  // Process each edge that crosses group boundaries
  const edgesToProcess = Array.from(edges.values()).filter(
    (edge) => edge.data?.sourceParentId !== edge.data?.targetParentId,
  );

  for (const edge of edgesToProcess) {
    // Find the effective source (parent if child is in collapsed group)
    let source = nodes.get(edge.source);
    if (source?.parentId && collapsed.has(source.parentId)) {
      source = nodes.get(source.parentId);
    }

    // Find the effective target (parent if child is in collapsed group)
    let target = nodes.get(edge.target);
    if (target?.parentId && collapsed.has(target.parentId)) {
      target = nodes.get(target.parentId);
    }

    // Remove the original edge
    edges.delete(edge.id);
    removeEdgeFromAdjacencyList(adjacencyList, edge.source, edge.target);

    // Create new edge if needed
    if (source && target && source.id !== target.id) {
      if (!wouldCreateCycle(adjacencyList, source.id, target.id)) {
        const newEdge = createEdge(source, target, edge.type as EdgeType);
        edges.set(newEdge.id, newEdge);
        addEdgeToAdjacencyList(adjacencyList, source.id, target.id);
      }
    }
  }

  // Remove child nodes of collapsed groups
  Array.from(nodes.values())
    .filter((node) => node.parentId && collapsed.has(node.parentId))
    .forEach((node) => nodes.delete(node.id));

  // Remove internal edges within collapsed groups
  Array.from(edges.values())
    .filter(
      (edge) =>
        edge.data?.sourceParentId === edge.data?.targetParentId &&
        edge.data?.sourceParentId &&
        collapsed.has(edge.data?.sourceParentId),
    )
    .forEach((edge) => {
      edges.delete(edge.id);
      removeEdgeFromAdjacencyList(adjacencyList, edge.source, edge.target);
    });
}
